import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  qAppConfig,
  qInventorySupplierById,
  qPurchaseOrderForReceive,
  qPurchaseOrderLinesByOrder,
  qPurchaseOrderPrintSettings,
  type AppConfigRow,
  type InventorySupplierRow,
  type PurchaseOrderLineRow,
  type PurchaseOrderPrintSettingsRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useSendEmail } from '../../hooks/useSendEmail';
import { buildPurchaseOrderEmail } from './buildPurchaseOrderEmail';

/**
 * PRD.241 — Review & Send Purchase Order email modal.
 *
 * Opens from the PO Details Drawer or the PO list. Loads the same data
 * the print mount uses, generates a supplier-facing HTML/text email, and
 * lets the user review/edit recipient + subject + intro before posting
 * to `psendEmail`. NO DB writes; NO PO mutation; NO stock movement.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string | null;
  portalStyle?: CSSProperties;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function SendPurchaseOrderEmailModal({
  open,
  onOpenChange,
  purchaseOrderId,
  portalStyle,
}: Props) {
  const poId = purchaseOrderId ?? '';
  const fmt = useFormatMoney();
  const sendEmail = useSendEmail();

  // PowerSync reads — same sources as PurchaseOrderPrintMount.
  const { data: poRows = [] } = usePowerSyncQuery<PurchaseOrderRow>(qPurchaseOrderForReceive(poId));
  const po = poId ? (poRows[0] ?? null) : null;

  const { data: lines = [] } = usePowerSyncQuery<PurchaseOrderLineRow>(
    qPurchaseOrderLinesByOrder(poId),
  );

  const supplierId = po?.supplier_id ?? '';
  const { data: supplierRows = [] } = usePowerSyncQuery<InventorySupplierRow>(
    qInventorySupplierById(supplierId),
  );
  const supplierMaster = supplierId ? (supplierRows[0] ?? null) : null;

  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const appConfig = appConfigRows[0] ?? null;

  const { data: settingsRows = [] } = usePowerSyncQuery<PurchaseOrderPrintSettingsRow>(
    qPurchaseOrderPrintSettings(),
  );
  const savedSettings = settingsRows[0] ?? null;

  // Editable form state. Seeding is gated by `seededForRef` (below) so we
  // don't need a separate dirty flag — once the modal has seeded the
  // initial values for a PO id, the user is free to edit them.
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  const [subject, setSubject] = useState('');
  const [intro, setIntro] = useState('');

  // Snapshot-style build that does NOT depend on the editable intro. Used
  // both for seeding the form (so the seed isn't a self-triggering loop)
  // and for the live summary preview. Recomputes only when actual PO
  // data changes, not on every keystroke.
  const built = useMemo(() => {
    if (!po) return null;
    return buildPurchaseOrderEmail({
      po,
      lines,
      supplierMaster,
      appConfig,
      savedSettings,
      formatMoney: fmt,
    });
  }, [po, lines, supplierMaster, appConfig, savedSettings, fmt]);

  // Track which (PO id, open) combination has already been seeded so we
  // don't fight the user once they start editing. Resets to null on
  // close so reopening always re-seeds with fresh PO data.
  const seededForRef = useRef<string | null>(null);

  // Seed defaults whenever the modal is open AND a new PO's data has
  // loaded AND we haven't seeded for that PO yet. PRD.241 §8.
  useEffect(() => {
    if (!open) return;
    if (!built) return;
    if (!po) return;
    const key = po.id;
    if (seededForRef.current === key) return;
    seededForRef.current = key;
    setToEmail(built.defaultRecipient?.email ?? '');
    setToName(built.defaultRecipient?.name ?? built.supplierName);
    setSubject(built.subject);
    setIntro(
      `Dear ${built.supplierName},\n\nPlease see our purchase order details below.\n\nThank you,\n${built.buyerName}`,
    );
  }, [open, built, po]);

  // Stable ref to the hook's reset so the close effect's deps stay tight
  // (the hook returns a new wrapper object on every render).
  const resetSendEmailRef = useRef(sendEmail.reset);
  resetSendEmailRef.current = sendEmail.reset;

  // Reset transient state when the modal closes. Only fires on the
  // open→false transition.
  useEffect(() => {
    if (open) return;
    seededForRef.current = null;
    setToEmail('');
    setToName('');
    setSubject('');
    setIntro('');
    resetSendEmailRef.current();
  }, [open]);

  const supplierEmailMissing = !built?.defaultRecipient && !toEmail;
  const recipientValid = EMAIL_RE.test(toEmail.trim());
  const canSend =
    sendEmail.enabled &&
    !!built &&
    recipientValid &&
    subject.trim().length > 0 &&
    intro.trim().length > 0 &&
    sendEmail.status !== 'sending';

  const handleSend = async () => {
    if (!built || !canSend) return;
    const editedEmail = buildPurchaseOrderEmail({
      po: po!,
      lines,
      supplierMaster,
      appConfig,
      savedSettings,
      introMessage: intro,
      formatMoney: fmt,
    });
    const result = await sendEmail.send({
      to: [{ email: toEmail.trim(), name: toName.trim() || undefined }],
      subject: subject.trim(),
      text: editedEmail.text,
      html: editedEmail.html,
    });
    if (result) {
      // Auto-close after a short success display.
      setTimeout(() => onOpenChange(false), 1200);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        if (sendEmail.status === 'sending') return;
        onOpenChange(v);
      }}
      title={built ? `Send Purchase Order ${built.poNumber}` : 'Send Purchase Order'}
      maxWidth={680}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={sendEmail.status === 'sending'}
          >
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={() => void handleSend()}
            disabled={!canSend}
          >
            {sendEmail.status === 'sending' ? 'Sending…' : 'Send Email'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5) var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        {!sendEmail.enabled && (
          <Banner tone="warning">
            Email service is not configured for this clinic. Ask an admin to set{' '}
            <code style={{ fontSize: 11 }}>VITE_EMAIL_NOTIFICATIONS_URL</code>.
          </Banner>
        )}

        {sendEmail.status === 'success' && <Banner tone="success">Email sent to {toEmail}.</Banner>}

        {sendEmail.status === 'error' && sendEmail.error && (
          <Banner tone="error">{sendEmail.error}</Banner>
        )}

        {supplierEmailMissing && (
          <Banner tone="warning">
            Supplier email is missing on the supplier master. Enter an email address to continue. We
            won't update the supplier record.
          </Banner>
        )}

        <Row label="To · Email">
          <input
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="supplier@example.com"
            style={inputStyle}
          />
          {toEmail && !recipientValid && (
            <span style={{ fontSize: 10, color: '#b91c1c' }}>Enter a valid email address.</span>
          )}
        </Row>
        <Row label="To · Name">
          <input
            type="text"
            value={toName}
            onChange={(e) => setToName(e.target.value)}
            placeholder="Supplier name"
            style={inputStyle}
          />
        </Row>
        <Row label="Subject">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={inputStyle}
          />
        </Row>
        <Row label="Intro message">
          <textarea
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            rows={5}
            style={{
              ...inputStyle,
              height: 'auto',
              padding: 'var(--sp-2) var(--sp-3)',
              resize: 'vertical',
              lineHeight: 1.5,
              fontFamily: 'inherit',
            }}
          />
          <HelpText>
            This greeting + closing wraps the PO details below. The PO line items and totals are
            generated automatically.
          </HelpText>
        </Row>

        <PurchaseOrderSummary
          po={po}
          lines={lines}
          formatMoney={fmt}
          buyerName={built?.buyerName}
          supplierName={built?.supplierName}
        />
      </div>
    </Modal>
  );
}

// ─── Summary preview ───

function PurchaseOrderSummary({
  po,
  lines,
  formatMoney,
  buyerName,
  supplierName,
}: {
  po: PurchaseOrderRow | null;
  lines: PurchaseOrderLineRow[];
  formatMoney: (amount: number) => string;
  buyerName?: string;
  supplierName?: string;
}) {
  if (!po) {
    return (
      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
        Loading purchase order…
      </div>
    );
  }
  const subtotal = lines.reduce((s, l) => s + (l.amount ?? 0), 0);
  const total = po.total_amount ?? subtotal;
  return (
    <div
      style={{
        marginTop: 'var(--sp-2)',
        padding: 'var(--sp-3)',
        background: 'var(--bg-surface-2, #f8fafc)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        Summary preview
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', lineHeight: 1.5 }}>
        From <strong>{buyerName ?? '—'}</strong> · To <strong>{supplierName ?? '—'}</strong>
        <br />
        {lines.length} line item{lines.length === 1 ? '' : 's'} · Total{' '}
        <strong>{formatMoney(total)}</strong>
      </div>
    </div>
  );
}

// ─── Local UI primitives ───

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HelpText({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5 }}>{children}</span>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'warning' | 'success' | 'error';
  children: React.ReactNode;
}) {
  const palette =
    tone === 'success'
      ? { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' }
      : tone === 'error'
        ? { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' }
        : { bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
  return (
    <div
      style={{
        padding: 'var(--sp-2) var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        fontSize: 'var(--font-size-xs)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: CSSProperties = {
  height: 36,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--input-bg, #ffffff)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  outline: 'none',
  fontFamily: 'inherit',
};
